/**
 * UpgradeCheckoutPage — /upgrade/checkout
 *
 * Subscription Summary page — route-based replacement for the proration
 * modal on UpgradePage. Sourced entirely from URL params so the URL is
 * bookmarkable / deep-linkable / shareable (e.g. promo emails can land
 * the merchant here with the plan + discount pre-applied).
 *
 * Behind flag: UPGRADE_CHECKOUT_V2 (see UpgradePage.js). When flag
 * is off, this route still exists but merchants never reach it —
 * UpgradePage keeps opening the modal instead.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axiosInstance from '../../axiosConfig';
import { useToast } from '../../components/ui/Toast';
import Shell from '../dashboard/Shell';
import { ArrowLeft, Loader2, Sparkles, ShieldCheck, Info, Tag, X } from 'lucide-react';
import './UpgradeCheckoutPage.css';

const LOCALE_MAP = {
  NGN: 'en-NG', USD: 'en-US', GBP: 'en-GB', EUR: 'de-DE',
  GHS: 'en-GH', KES: 'en-KE', ZAR: 'en-ZA',
};

const formatPrice = (amount, currency = 'NGN') => {
  const locale = LOCALE_MAP[currency] || 'en-NG';
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(Number(amount || 0));
};

export default function UpgradeCheckoutPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const toast = useToast();

  const targetTier = params.get('tier');
  const action = params.get('action') === 'downgrade' ? 'downgrade' : 'upgrade';
  const tierName = params.get('name') || targetTier || 'plan';
  // Optional pre-applied promo code via URL — enables promo emails like
  // /upgrade/checkout?tier=PRO&discountCode=TRIAL26 to land the merchant
  // in a discount-applied state immediately.
  const initialPromo = params.get('discountCode') || params.get('promoCode') || '';

  const [loading, setLoading] = useState(true);
  const [proration, setProration] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState(null);

  // Promo code state:
  //   promoInput — what the merchant types
  //   appliedPromo — what's actually been validated + applied to `proration`
  //   applyingPromo — in-flight state for the Apply button
  const [promoInput, setPromoInput] = useState(initialPromo);
  const [appliedPromo, setAppliedPromo] = useState(initialPromo || null);
  const [applyingPromo, setApplyingPromo] = useState(false);
  const [promoError, setPromoError] = useState(null);

  // Fetch proration whenever the target tier or the currently-applied promo
  // changes. Applied promo (not the input) triggers this so the number the
  // merchant sees is always in sync with what the pay button will charge.
  useEffect(() => {
    if (!targetTier) {
      setError('No plan selected. Please pick a plan from the upgrade page.');
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await axiosInstance.post('/merchant/billing/calculate-proration', {
          newTier: targetTier,
          action,
          ...(appliedPromo ? { promoCode: appliedPromo } : {}),
        });
        if (cancelled) return;
        if (res.data?.success) {
          setProration(res.data);
        } else {
          throw new Error(res.data?.error || 'Failed to calculate pricing');
        }
      } catch (err) {
        if (cancelled) return;
        console.error('[UpgradeCheckout] proration failed', err);
        setError(err.response?.data?.error || err.message || 'Failed to calculate pricing');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [targetTier, action, appliedPromo]);

  const handleApplyPromo = useCallback(async () => {
    const code = promoInput.trim();
    if (!code) return;
    setApplyingPromo(true);
    setPromoError(null);
    try {
      // Same endpoint the useEffect uses — if the code is invalid the
      // backend echoes an error we surface as an inline message instead
      // of updating the applied state.
      const res = await axiosInstance.post('/merchant/billing/calculate-proration', {
        newTier: targetTier,
        action,
        promoCode: code,
      });
      if (res.data?.success) {
        setAppliedPromo(code);
        setProration(res.data);
      } else {
        setPromoError(res.data?.error || 'Invalid or expired promo code');
      }
    } catch (err) {
      setPromoError(err.response?.data?.error || 'Failed to apply code');
    } finally {
      setApplyingPromo(false);
    }
  }, [promoInput, targetTier, action]);

  const handleRemovePromo = useCallback(() => {
    setPromoInput('');
    setAppliedPromo(null);
    setPromoError(null);
  }, []);

  const handleConfirm = useCallback(async () => {
    if (!targetTier || !proration) return;
    setConfirming(true);
    try {
      const res = await axiosInstance.post('/merchant/billing/initiate-tier-change', {
        newTier: targetTier,
        action,
        amount: proration.amount,
        currency: proration.currency || 'NGN',
        requiresPayment: proration.requiresPayment,
        isFreeTarget: proration.isFreeTarget,
        // Pass the promo through so the backend can attach it to the
        // Paystack transaction metadata and mark the redemption on verify.
        ...(appliedPromo ? { promoCode: appliedPromo } : {}),
      });
      if (res.data?.success) {
        if (res.data.noPaymentRequired) {
          toast.success(proration.explanation || 'Plan changed successfully.');
          navigate('/upgrade');
        } else if (res.data.authorizationUrl) {
          window.location.href = res.data.authorizationUrl;
        } else {
          throw new Error('Invalid response from server');
        }
      }
    } catch (err) {
      console.error('[UpgradeCheckout] confirm failed', err);
      toast.error(err.response?.data?.error || 'Failed to process tier change. Please try again.');
    } finally {
      setConfirming(false);
    }
  }, [targetTier, action, proration, appliedPromo, navigate, toast]);

  const title = action === 'downgrade' ? `Downgrade to ${tierName}` : `Upgrade to ${tierName}`;

  return (
    <Shell>
      <div className="upc-page">
        <button
          type="button"
          className="upc-back"
          onClick={() => navigate('/upgrade')}
        >
          <ArrowLeft size={16} />
          <span>Back to plans</span>
        </button>

        <div className="upc-card">
          <header className="upc-header">
            <h1 className="upc-title">{title}</h1>
            <p className="upc-sub">Review the summary below and confirm to continue.</p>
          </header>

          {loading ? (
            <div className="upc-loading">
              <Loader2 size={18} className="upc-spin" />
              <span>Calculating pricing…</span>
            </div>
          ) : error ? (
            <div className="upc-error">
              <p>{error}</p>
              <button className="upc-btn upc-btn-secondary" onClick={() => navigate('/upgrade')}>
                Back to plans
              </button>
            </div>
          ) : proration ? (
            <>
              <div className="upc-summary">
                <div className="upc-summary-row upc-summary-row-headline">
                  <span className="upc-summary-label">
                    {proration.requiresPayment ? 'Amount to pay' : 'No payment required'}
                  </span>
                  <span className="upc-summary-value">
                    {proration.requiresPayment
                      ? formatPrice(proration.amount, proration.currency)
                      : '—'}
                  </span>
                </div>

                {proration.requiresPayment && proration.daysRemaining > 0 && (
                  <div className="upc-summary-row">
                    <span>Days remaining in current period</span>
                    <strong>{proration.daysRemaining} days</strong>
                  </div>
                )}

                {!proration.requiresPayment && proration.creditAmount > 0 && (
                  <div className="upc-summary-row">
                    <span>Credit to apply next billing period</span>
                    <strong>{formatPrice(proration.creditAmount, proration.currency)}</strong>
                  </div>
                )}

                {proration.explanation && (
                  <div className="upc-note">
                    <Sparkles size={14} />
                    <span>{proration.explanation}</span>
                  </div>
                )}
              </div>

              {/* Discount code — only for paid transitions. Downgrades to
                  FREE / no-payment states have nothing to discount. */}
              {proration.requiresPayment && (
                <div className="upc-promo">
                  <label className="upc-promo-label">Discount code</label>
                  {appliedPromo ? (
                    <div className="upc-promo-applied">
                      <Tag size={14} />
                      <span className="upc-promo-code">{appliedPromo}</span>
                      <span className="upc-promo-check">Applied</span>
                      <button
                        type="button"
                        className="upc-promo-remove"
                        onClick={handleRemovePromo}
                        aria-label="Remove discount code"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ) : (
                    <>
                      <div className="upc-promo-row">
                        <input
                          type="text"
                          className="upc-promo-input"
                          placeholder="Enter code"
                          value={promoInput}
                          onChange={(e) => { setPromoInput(e.target.value); setPromoError(null); }}
                          onKeyDown={(e) => e.key === 'Enter' && !applyingPromo && handleApplyPromo()}
                          disabled={applyingPromo}
                          autoCapitalize="characters"
                        />
                        <button
                          type="button"
                          className="upc-promo-apply"
                          onClick={handleApplyPromo}
                          disabled={applyingPromo || !promoInput.trim()}
                        >
                          {applyingPromo ? <Loader2 size={13} className="upc-spin" /> : 'Apply'}
                        </button>
                      </div>
                      {promoError && <p className="upc-promo-error">{promoError}</p>}
                    </>
                  )}
                </div>
              )}

              <div className="upc-secure">
                <ShieldCheck size={14} />
                <span>Payment secured by Paystack</span>
              </div>

              <div className="upc-actions">
                <button
                  type="button"
                  className="upc-btn upc-btn-secondary"
                  onClick={() => navigate('/upgrade')}
                  disabled={confirming}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="upc-btn upc-btn-primary"
                  onClick={handleConfirm}
                  disabled={confirming}
                >
                  {confirming
                    ? <><Loader2 size={14} className="upc-spin" /> Processing…</>
                    : proration.isFreeTarget ? 'Confirm downgrade'
                    : proration.requiresPayment
                      ? `Pay ${formatPrice(proration.amount, proration.currency)}`
                      : 'Confirm change'}
                </button>
              </div>
            </>
          ) : (
            <div className="upc-error">
              <Info size={14} />
              <span>No pricing information available. Please try again.</span>
            </div>
          )}
        </div>
      </div>
    </Shell>
  );
}
